import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Article,
  CheckCircle,
  FilePdf,
  FilmStrip,
  Images,
  Palette,
  PaperPlaneTilt,
  Sparkle,
  UsersThree,
} from '@phosphor-icons/react';

const SCREEN_DELAY_MS = 3600;

const SCREENS = [
  { id: 'client', label: 'Client Brain', icon: UsersThree },
  { id: 'dna', label: 'Design DNA', icon: Palette },
  { id: 'master', label: 'Content Master', icon: Article },
  { id: 'carousel', label: 'Carousel Studio', icon: Images },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

function ClientBrainPanel() {
  return (
    <div className="grid h-full gap-3 lg:grid-cols-[1.15fr_.85fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-indigo-300">Client Brain</p>
            <p className="mt-1 text-sm font-semibold text-white">Atelier Forma</p>
          </div>
          <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-[9px] font-semibold text-emerald-300">attivo</span>
        </div>
        <div className="mt-5 rounded-xl border border-white/[.06] bg-white/[.025] p-4">
          <p className="text-[9px] font-semibold uppercase tracking-[.14em] text-white/35">Chi è</p>
          <p className="mt-2 max-w-lg text-xs leading-5 text-slate-400">
            Studio creativo premium. Voce diretta, colta, contemporanea. Priorità: autorevolezza senza distanza.
          </p>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {['Autorevolezza', 'Chiarezza', 'Contemporaneo', 'Premium'].map(item => (
            <div key={item} className="flex items-center gap-2 rounded-lg border border-white/[.05] bg-white/[.02] px-3 py-2 text-[10px] text-slate-300">
              <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" /> {item}
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-fuchsia-300">Campagna corrente</p>
          <p className="mt-2 text-sm font-semibold">Settembre 2026</p>
          <div className="mt-4 space-y-2">
            {[78, 92, 64].map(width => <div key={width} className="h-1.5 rounded-full bg-white/10" style={{ width: `${width}%` }} />)}
          </div>
        </div>
        <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[.06] p-4">
          <div className="flex items-center gap-2 text-[10px] font-semibold text-fuchsia-200">
            <Sparkle size={14} weight="fill" /> Memoria condivisa
          </div>
          <p className="mt-2 text-[10px] leading-5 text-slate-400">Il contesto resta collegato a nuovi contenuti e campagne.</p>
        </div>
      </div>
    </div>
  );
}

function DesignDnaPanel() {
  return (
    <div className="grid h-full gap-3 lg:grid-cols-[.72fr_1.28fr]">
      <div className="space-y-3">
        <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-indigo-300">Design DNA</p>
          <p className="mt-1 text-xs font-semibold">Sistema visivo</p>
          <div className="mt-4 flex gap-2">
            {['#111827', '#8B5CF6', '#EC4899', '#F8FAFC'].map((value, index) => (
              <span
                key={value}
                className="h-8 flex-1 rounded-lg border border-white/10"
                style={{ backgroundColor: value, boxShadow: index === 1 ? '0 0 20px rgba(139,92,246,.22)' : 'none' }}
              />
            ))}
          </div>
          <div className="mt-5 space-y-3 text-[10px] text-slate-400">
            <div className="flex items-center justify-between"><span>H1</span><span className="font-semibold text-white">Inter / 72</span></div>
            <div className="flex items-center justify-between"><span>Paragraph</span><span className="font-semibold text-white">Inter / 28</span></div>
            <div className="flex items-center justify-between"><span>Ancoraggio</span><span className="rounded-md bg-white/[.05] px-2 py-1 text-white">Bottom</span></div>
          </div>
        </div>
        <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-cyan-300">Reference</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[1, 2, 3].map(item => <div key={item} className="aspect-[4/5] rounded-lg border border-white/[.06] bg-gradient-to-br from-white/[.08] to-white/[.02]" />)}
          </div>
        </div>
      </div>
      <div className="relative overflow-hidden rounded-2xl border border-white/[.08] bg-[#0b1020] p-4">
        <div className="absolute right-4 top-4 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-[8px] font-semibold uppercase tracking-[.12em] text-emerald-300">live</div>
        <div className="mx-auto flex aspect-[4/5] h-full max-h-[335px] w-auto max-w-full flex-col justify-end overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#201238] via-[#111827] to-[#090d16] p-6 shadow-2xl shadow-black/30">
          <span className="mb-3 w-fit rounded-full border border-fuchsia-300/30 bg-fuchsia-400/10 px-2 py-1 text-[8px] font-semibold uppercase tracking-[.18em] text-fuchsia-200">Ritratto consapevole</span>
          <p className="max-w-[90%] text-2xl font-black leading-[.95] tracking-[-.04em] text-white md:text-3xl">La relazione cambia il ritratto.</p>
          <div className="mt-4 h-px w-12 bg-fuchsia-400" />
          <p className="mt-3 max-w-[88%] text-[10px] leading-4 text-white/55">Il template segue il DNA. Il contenuto si adatta senza perdere identità.</p>
        </div>
      </div>
    </div>
  );
}

function ContentMasterPanel() {
  return (
    <div className="grid h-full gap-3 lg:grid-cols-[1.3fr_.7fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-fuchsia-300">Content Master</p>
            <p className="mt-1 text-sm font-semibold text-white">La relazione prima della posa</p>
          </div>
          <CheckCircle size={19} weight="fill" className="text-emerald-400" />
        </div>
        <div className="mt-5 space-y-3">
          <div className="h-2.5 w-[78%] rounded-full bg-white/15" />
          {[96, 88, 92, 70].map(width => <div key={width} className="h-1.5 rounded-full bg-white/[.07]" style={{ width: `${width}%` }} />)}
          <div className="my-5 h-px bg-white/[.06]" />
          <div className="h-2.5 w-[54%] rounded-full bg-white/12" />
          {[91, 84, 75].map(width => <div key={width} className="h-1.5 rounded-full bg-white/[.07]" style={{ width: `${width}%` }} />)}
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-indigo-300">Deriva output</p>
          <div className="mt-3 space-y-2">
            {[[Images, 'Carousel'], [FilmStrip, 'Reel'], [FilePdf, 'PDF']].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-white/[.06] bg-white/[.025] p-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-400/10 text-fuchsia-300"><Icon size={16} /></div>
                <div className="flex-1"><p className="text-[10px] font-semibold text-white">{label}</p><div className="mt-1.5 h-1 w-2/3 rounded-full bg-white/10" /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-400/15 bg-emerald-400/[.05] p-4">
          <p className="text-[9px] font-semibold uppercase tracking-[.14em] text-emerald-300">1 fonte</p>
          <p className="mt-1 text-[10px] leading-5 text-slate-400">Le revisioni restano nel Master e alimentano i derivati.</p>
        </div>
      </div>
    </div>
  );
}

function CarouselPanel() {
  const slides = [
    ['01', 'Perché il soggetto sembra rigido?'],
    ['02', 'Non è solo un problema tecnico.'],
    ['03', 'Costruisci fiducia prima di scattare.'],
    ['04', 'Un passo indietro crea sicurezza.'],
  ];

  return (
    <div className="grid h-full gap-3 lg:grid-cols-[190px_minmax(0,1fr)]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-3">
        <p className="px-1 text-[9px] font-bold uppercase tracking-[.16em] text-fuchsia-300">Carousel Studio</p>
        <div className="mt-3 space-y-2">
          {slides.map(([number, title], index) => (
            <div key={number} className={`rounded-xl border p-2.5 transition-colors ${index === 0 ? 'border-fuchsia-400/35 bg-fuchsia-400/[.08]' : 'border-white/[.05] bg-white/[.02]'}`}>
              <div className="flex items-center gap-2"><span className="text-[8px] font-bold text-white/30">{number}</span><span className="h-1 flex-1 rounded-full bg-white/10" /></div>
              <p className="mt-2 line-clamp-2 text-[9px] leading-4 text-slate-400">{title}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="relative flex min-h-[300px] items-center justify-center overflow-hidden rounded-2xl border border-white/[.07] bg-[#0c1323] p-5">
        <div className="absolute left-4 top-4 flex items-center gap-2 text-[9px] text-slate-500"><span className="h-2 w-2 rounded-full bg-emerald-400" /> FIT</div>
        <div className="relative aspect-[4/5] h-[285px] max-h-full overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#f8f4ee] to-[#ded7cf] p-6 text-[#181818] shadow-2xl shadow-black/30">
          <p className="text-[8px] font-bold uppercase tracking-[.18em] text-[#8a4f83]">Ritratto consapevole</p>
          <p className="mt-8 text-2xl font-black leading-[.96] tracking-[-.045em]">Perché il tuo soggetto sembra rigido?</p>
          <p className="mt-5 text-[9px] leading-4 text-black/55">Ogni scatto può rivelare l’essenza di una persona, ma prima serve una relazione reale.</p>
          <div className="absolute bottom-5 left-6 right-6 flex items-center justify-between border-t border-black/10 pt-3 text-[7px] font-semibold uppercase tracking-[.14em] text-black/35">
            <span>Sketchario</span><span>01 / 07</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedNumber({ value, start, reduced }) {
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (!start) return undefined;
    if (reduced || value === 0) {
      setDisplay(value);
      return undefined;
    }

    let frame;
    const startedAt = performance.now();
    const duration = 850;
    const tick = now => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduced, start, value]);

  return <span>{display}</span>;
}

function StatsStrip({ reduced }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const stats = [
    [1, 'Content Master'],
    [4, 'output collegati'],
    [5, 'fasi del workflow'],
    [0, 'carta per iniziare'],
  ];

  return (
    <div ref={ref} className="mx-auto mt-6 grid max-w-5xl grid-cols-2 overflow-hidden rounded-2xl border border-white/[.07] bg-white/[.02] md:grid-cols-4">
      {stats.map(([value, label], index) => (
        <div key={label} className={`px-4 py-4 text-center md:px-6 md:py-5 ${index % 2 ? 'border-l border-white/[.06]' : ''} ${index > 1 ? 'border-t border-white/[.06] md:border-t-0 md:border-l' : ''}`}>
          <p className="text-2xl font-black tracking-[-.04em] text-white md:text-3xl"><AnimatedNumber value={value} start={visible} reduced={reduced} /></p>
          <p className="mt-1 text-[9px] font-semibold uppercase tracking-[.14em] text-slate-500">{label}</p>
        </div>
      ))}
    </div>
  );
}

export default function LandingMotionShowcase() {
  const reduced = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const active = SCREENS[activeIndex];

  useEffect(() => {
    if (reduced) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex(index => (index + 1) % SCREENS.length);
    }, SCREEN_DELAY_MS);
    return () => window.clearInterval(timer);
  }, [reduced]);

  const panel = useMemo(() => {
    if (active.id === 'client') return <ClientBrainPanel />;
    if (active.id === 'dna') return <DesignDnaPanel />;
    if (active.id === 'master') return <ContentMasterPanel />;
    return <CarouselPanel />;
  }, [active.id]);

  return (
    <div className="relative mx-auto w-full max-w-6xl" data-testid="landing-motion-showcase">
      <div className="absolute -inset-10 rounded-[3rem] bg-gradient-to-r from-indigo-500/10 via-fuchsia-500/10 to-cyan-400/10 blur-3xl" />
      <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#0d1527] shadow-2xl shadow-black/40">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-rose-400/55" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300/55" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400/55" /></div>
          <span className="text-[9px] font-semibold uppercase tracking-[.22em] text-white/35">Sketchario · live workflow</span>
          <div className="flex items-center gap-1.5 text-[8px] text-white/30"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> online</div>
        </div>

        <div className="grid min-h-[400px] gap-0 md:grid-cols-[190px_minmax(0,1fr)]">
          <div className="border-b border-white/[.06] bg-[#0a1120] p-3 md:border-b-0 md:border-r md:p-4">
            <p className="mb-3 px-2 text-[8px] font-bold uppercase tracking-[.18em] text-white/25">Workflow</p>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
              {SCREENS.map((screen, index) => {
                const Icon = screen.icon;
                const selected = index === activeIndex;
                return (
                  <button
                    key={screen.id}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className={`relative flex items-center gap-2 overflow-hidden rounded-xl border px-3 py-2.5 text-left text-[10px] transition-all duration-500 ${selected ? 'border-fuchsia-400/35 bg-fuchsia-500/10 text-white shadow-lg shadow-fuchsia-950/10' : 'border-white/[.05] bg-white/[.02] text-white/45 hover:bg-white/[.04] hover:text-white/70'}`}
                    aria-pressed={selected}
                  >
                    <Icon size={15} weight={selected ? 'fill' : 'regular'} className={selected ? 'text-fuchsia-300' : ''} />
                    <span className="truncate">{screen.label}</span>
                    {selected && !reduced && <span className="absolute bottom-0 left-0 h-px bg-fuchsia-400" style={{ animation: `landingProgress ${SCREEN_DELAY_MS}ms linear forwards` }} />}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 hidden rounded-xl border border-white/[.05] bg-white/[.015] p-3 md:block">
              <div className="flex items-center gap-2 text-[9px] font-semibold text-slate-400"><PaperPlaneTilt size={13} className="text-cyan-300" /> Publishing</div>
              <div className="mt-2 flex items-center gap-2 text-[8px] text-slate-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400/70" /> Queue pronta</div>
            </div>
          </div>

          <div className="relative overflow-hidden p-3 md:p-5">
            <div key={active.id} className={reduced ? 'h-full' : 'h-full animate-[landingPanelIn_.5s_ease-out]'}>
              {panel}
            </div>
            <div className="pointer-events-none absolute bottom-3 right-4 rounded-full border border-white/[.06] bg-[#080d18]/70 px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[.12em] text-white/30 backdrop-blur">
              {activeIndex + 1} / {SCREENS.length}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes landingPanelIn {
          from { opacity: 0; transform: translateY(8px) scale(.995); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes landingProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <StatsStrip reduced={reduced} />
    </div>
  );
}
